import { useMemo, useState } from "react";
import { MdKeyboardArrowDown } from "react-icons/md";
import { getAuditLevelLabel } from "../../../utils";
import styles from "../style.module.scss";

interface OrgTab {
  label: string;
  value: number;
  logo: string;
}

interface AuditInfoAccordionProps {
  auditDetails: any;
  orgTabs: OrgTab[];
  activeOrg: string;
  onTabChange: (org: OrgTab) => void;
  isAccordionOpen: boolean;
  onToggleAccordion: () => void;
}

const SIDE_TABS = ["Audit Preference", "Auditee Info", "Auditor Info" , "Data Collection Stages",];

const AuditInfoAccordion = ({
  auditDetails,
  orgTabs,
  activeOrg,
  onTabChange,
  isAccordionOpen,
  onToggleAccordion,
}: AuditInfoAccordionProps) => {
  const [activeSection, setActiveSection] = useState("Audit Preference");

  const orgData: any = useMemo(() => {
    const data: any = {};
    auditDetails?.organizations?.forEach((org: any) => {
      data[org.organizationName] = {
        industry: org.industryOrSector,
        organization: org.organizationName,
        email: org.organizationEmail,
        applications: org.applications,
        lead:
          org.auditeeTeamLead?.length > 0
            ? org.auditeeTeamLead
                .map((lead: any) => `${lead.name} – ${lead.email}`)
                .join(", ")
            : "—",
        members:
          org.auditeeMembers?.length > 0
            ? org.auditeeMembers.map((m: any) => `${m.name} – ${m.email}`)
            : ["—"],
      };
    });
    return data;
  }, [auditDetails]);

  const renderSection = () => {
    switch (activeSection) {
      case "Auditee Info":
        return (
          <>
            <h6 className={styles.titleInfo}>Auditee Information</h6>
            <table className={styles.infoTable}>
              <thead className="visually-hidden">
                <tr>
                  <th>Column 1</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className={styles.label}>Industry/ Sector</td>
                  <td className={styles.value}>
                    {orgData[activeOrg]?.industry}
                  </td>
                </tr>
                <tr>
                  <td className={styles.label}>Organization</td>
                  <td className={styles.value}>
                    {orgData[activeOrg]?.organization}
                  </td>
                </tr>
                <tr>
                  <td className={styles.label}>Applications</td>
                  <td className={styles.value}>
                    {orgData[activeOrg]?.applications
                      ?.map((app: any) => app?.applicationName)
                      .join(", ")}
                  </td>
                </tr>
                <tr>
                  <td className={styles.label}>Organization Email</td>
                  <td className={styles.value}>{orgData[activeOrg]?.email}</td>
                </tr>
                <tr>
                  <td className={styles.label}>Auditee Team Lead</td>
                  <td className={styles.value}>{orgData[activeOrg]?.lead}</td>
                </tr>
                <tr>
                  <td className={styles.label}>Auditee Members</td>
                  <td className={styles.value}>
                    {orgData[activeOrg]?.members?.map((m: string) => (
                      <div key={m}>{m}</div>
                    ))}
                  </td>
                </tr>
              </tbody>
            </table>
          </>
        );

      case "Audit Preference":
        return (
          <>
            <h6 className={styles.titleInfo}>Audit Preference</h6>
            <table className={styles.infoTable}>
              <thead className="visually-hidden">
                <tr>
                  <th>Column 1</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className={styles.label}>Audit Themes</td>
                  <td className={styles.value}>
                    {auditDetails?.auditTheme
                      ?.map((t: any) => `${t.themeName} (${t.themeCode})`)
                      .join(", ")}
                  </td>
                </tr>
                <tr>
                  <td className={styles.label}>Audit Level</td>
                  <td className={styles.value}>
                    {getAuditLevelLabel(auditDetails?.auditLevel)} (
                    <span>{auditDetails?.auditLevel}</span>)
                  </td>
                </tr>
                <tr>
                  <td className={styles.label}>Duration</td>
                  <td className={styles.value}>
                    {auditDetails?.startDate?.split("-")?.reverse()?.join("/")}{" "}
                    - {auditDetails?.endDate?.split("-")?.reverse()?.join("/")}
                  </td>
                </tr>
                <tr>
                  <td className={styles.label}>Response Time</td>
                  <td className={styles.value}>
                    {auditDetails?.responseTime} Days
                  </td>
                </tr>
                <tr>
                  <td className={styles.label}>Reminder Alert</td>
                  <td className={styles.value}>
                    {auditDetails?.escalationFrequency} Days
                  </td>
                </tr>
              </tbody>
            </table>
          </>
        );

      case "Auditor Info":
        return (
          <>
            <h6 className={styles.titleInfo}>Auditor Information</h6>
            <table className={styles.infoTable}>
              <thead className="visually-hidden">
                <tr>
                  <th>Column 1</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className={styles.label}>Auditor Name</td>
                  <td className={styles.value}>{auditDetails?.auditorName}</td>
                </tr>
                <tr>
                  <td className={styles.label}>Auditor Team Members</td>
                  <td className={styles.value}>
                    {auditDetails?.auditorMembers?.map((a: any) => (
                      <div key={a.id}>
                        {a.name} – {a.email}
                      </div>
                    ))}
                  </td>
                </tr>
              </tbody>
            </table>
          </>
        );

      case "Data Collection Stages":
        return (
          <>
            <h6 className={styles.titleInfo}>Data Collection Stages</h6>
            <table className={`${styles.infoTable} ${styles.centerAlignedTable}`}>
              <thead>
                <tr>
                  <th className={`${styles.label} ${styles.noColumn}`}>Data Collection Order</th>
                  <th className={styles.label}>Data Collection Stages Name</th>
                  <th className={styles.label}>Auditor Review</th>
                </tr>
              </thead>
              <tbody>
                {auditDetails?.auditSequences?.map((a: any, index: number) => {
                  return (
                    <tr key={a.sequence?.sequenceOrderId || index}>
                      <td className={`${styles.value} ${styles.noColumn}`}>
                        {a.sequenceOrderId}
                      </td>
                      <td className={styles.value}>
                        {a.sequence?.length > 0
                          ? a.sequence.map((s: { sequenceName: string }) => s.sequenceName).join(", ")
                          : "-"}
                      </td>
                      <td className={styles.value}>
                        {a.questionValidationRequired && a.responseValidationRequired === true ? "Before Sending Questions, After AI review responses" :
                          a.questionValidationRequired === true
                            ? "Before Sending Questions"
                            : a.responseValidationRequired
                              ? "After AI review responses"
                              : !a.questionValidationRequired && !a.responseValidationRequired
                                ? "Fully automated(No manual review)"
                                : "-"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        );
      default:
        return null;
    }
  };

  const handleTabClick = (org: OrgTab) => {
    onTabChange(org);
    setActiveSection("Audit Preference");
  };

  return (
    <div className={styles.wrapper}>
      {/* Accordion Header with Org Tabs */}
      <div
        className={styles.accordionHeader}
        onClick={onToggleAccordion}
        onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          onToggleAccordion();
        }
      }}
      role="button"
      tabIndex={0}
      >
        <div className={styles.tabContainer}>
          {orgTabs.map((org, index) => {
            const isActive = activeOrg === org.label;
            return (
              <div
                key={org.value}
                className={`${styles.tabItemWrapper} ${
                  isActive ? styles.activeTab : ""
                } ${styles[`tabItem_${index}`]}`}
              >
                <button
                  className={`${styles.tabBtn} ${
                    isActive ? styles.activeTabBtn : ""
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTabClick(org);
                  }}
                >
                  <img
                    src={org.logo}
                    className={styles.tabIcon}
                    alt="no"
                  />
                  <span>{org.label}</span>
                </button>

                {index < orgTabs.length - 1 &&
                  !isActive &&
                  !(activeOrg === orgTabs[index + 1].label) && (
                    <div className={styles.tabDivider} />
                  )}
              </div>
            );
          })}
        </div>
        <div className={styles.accordionToggle}>
          <MdKeyboardArrowDown
            size={22}
            className={`${styles.questionArrow} ${
              isAccordionOpen ? styles.chevronUp : ""
            }`}
          />
        </div>
      </div>

      {/* Accordion Content - Sidebar Tabs & Section Content */}
      {isAccordionOpen && (
        <div className="d-flex mt-3">
          <div className={styles.sidebar}>
            {SIDE_TABS.map((tab) => (
              <button
                key={tab}
                className={`${styles.sideBtn} ${
                  activeSection === tab ? styles.activeSideBtn : ""
                }`}
                onClick={() => setActiveSection(tab)}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Content Area */}
          <div className={styles.contentInfo}>{renderSection()}</div>
        </div>
      )}
    </div>
  );
};

export default AuditInfoAccordion;
